import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../store/store';
import {
  claimInstitutionalAccess,
  getInstitutionalClaimOptions,
} from '../../../../services/institutionalAccess';
import { getSubscriptionStatus } from '../../../../services/subscriptions';
import { getUserInfo } from '../../../../services/auth';
import { InstitutionalClaimOptions } from '../../../../types/InstitutionalAccess';
import InstitutionalAccessClaimModal from './InstitutionalAccessClaimModal';
import InstitutionalAccessExpiredModal from './InstitutionalAccessExpiredModal';
import {
  isTipDismissed,
  recordTipDismissed,
} from '../../../../helpers/messageStateGating';
import type { MessageKey } from '../../../../config/messageRegistry';

const formatAccessDate = (value: string) =>
  new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(new Date(value));

const getExpiredDismissalKey = ({
  userId,
  contractId,
  endedAt,
}: {
  userId: string;
  contractId: string;
  endedAt: string;
}) => `institutional-access-expired:${userId}:${contractId}:${endedAt}`;

/** Not Now on the confirm-email state hides it for this browser session. */
export const confirmEmailDismissedKey = (userId: string) =>
  `ora:confirm-email-access-dismissed:${userId}`;

const readConfirmEmailDismissed = (userId: string | undefined) => {
  if (!userId) return false;
  try {
    return Boolean(sessionStorage.getItem(confirmEmailDismissedKey(userId)));
  } catch {
    return false;
  }
};

const InstitutionalAccessModals = () => {
  const currentUser = useSelector((state: RootState) => state.user.profile);

  const subscription = useSelector(
    (state: RootState) => state.subscription.subscription
  );

  const [claimOptions, setClaimOptions] =
    useState<InstitutionalClaimOptions | null>(null);

  const [isClaiming, setIsClaiming] = useState(false);
  const [claimError, setClaimError] = useState<string | null>(null);

  const [dismissedExpiredKey, setDismissedExpiredKey] = useState<string | null>(
    null
  );

  const [isConfirmEmailDismissed, setIsConfirmEmailDismissed] = useState(() =>
    readConfirmEmailDismissed(currentUser?.id)
  );

  useEffect(() => {
    setIsConfirmEmailDismissed(readConfirmEmailDismissed(currentUser?.id));
  }, [currentUser?.id]);

  useEffect(() => {
    let isMounted = true;

    const loadClaimOptions = async () => {
      if (!currentUser?.id) {
        setClaimOptions(null);
        return;
      }

      const options = await getInstitutionalClaimOptions();
      if (isMounted) {
        setClaimOptions(options);
      }
    };

    void loadClaimOptions();

    return () => {
      isMounted = false;
    };
  }, [currentUser?.id]);

  const expiredDismissalKey = useMemo(() => {
    const expiredAccess = subscription?.expiredInstitutionalAccess;
    if (!currentUser?.id || !expiredAccess) return null;

    return getExpiredDismissalKey({
      userId: currentUser.id,
      contractId: expiredAccess.contractId,
      endedAt: expiredAccess.endedAt,
    });
  }, [currentUser?.id, subscription?.expiredInstitutionalAccess]);

  useEffect(() => {
    if (!expiredDismissalKey) {
      setDismissedExpiredKey(null);
      return;
    }

    setDismissedExpiredKey(
      localStorage.getItem(expiredDismissalKey) ? expiredDismissalKey : null
    );
  }, [expiredDismissalKey]);

  // ORA-994: a roster seat waits for a confirmed email; the modal asks for
  // it, then turns into the claim once the email is confirmed.
  const emailVerificationRequired = Boolean(
    !claimOptions?.hasClaimableAccess && claimOptions?.emailVerificationRequired
  );

  const showClaimModal = Boolean(
    currentUser?.id &&
    (claimOptions?.hasClaimableAccess ||
      (emailVerificationRequired && !isConfirmEmailDismissed))
  );

  const expiredAccess = subscription?.expiredInstitutionalAccess ?? null;

  // Per-instance dismissal key: one message-state row per (contract, endedAt),
  // so a contract that expires again later re-shows. userId is not in the key —
  // rows are already per-user in the store.
  const expiredMessageKey: MessageKey | null = expiredAccess
    ? `institutional-expired:${expiredAccess.contractId}:${expiredAccess.endedAt}`
    : null;

  const isExpiredDismissedInStore = useSelector((state: RootState) =>
    expiredMessageKey
      ? isTipDismissed(state, state.user.profile, expiredMessageKey)
      : false
  );

  const showExpiredModal = Boolean(
    currentUser?.id &&
    expiredAccess &&
    expiredDismissalKey &&
    dismissedExpiredKey !== expiredDismissalKey &&
    !isExpiredDismissedInStore &&
    !showClaimModal
  );

  const schoolNames = useMemo(() => {
    const names = new Set(
      claimOptions?.contracts?.map((contract) => contract.schoolName) ?? []
    );

    return Array.from(names).join(', ');
  }, [claimOptions?.contracts]);

  const accessEndDate = claimOptions?.contracts?.[0]?.endsAt
    ? formatAccessDate(claimOptions.contracts[0].endsAt)
    : null;

  const expiredEndedDate = expiredAccess?.endedAt
    ? formatAccessDate(expiredAccess.endedAt)
    : null;

  const handleClaim = async () => {
    if (isClaiming) return;
    setIsClaiming(true);
    setClaimError(null);

    const result = await claimInstitutionalAccess();
    if (!result?.claimed) {
      const options = await getInstitutionalClaimOptions();
      setClaimOptions(options);

      setClaimError(
        options?.hasClaimableAccess
          ? 'We could not claim access yet. Try again.'
          : null
      );

      setIsClaiming(false);
      return;
    }

    await Promise.all([getSubscriptionStatus(), getUserInfo()]);
    const options = await getInstitutionalClaimOptions();
    setClaimOptions(options?.hasClaimableAccess ? options : null);
    setIsClaiming(false);
  };

  /** Resolves true while the email still needs confirming. */
  const recheckEmailVerification = async () => {
    const options = await getInstitutionalClaimOptions();
    // A failed request keeps the modal as it is.
    if (!options) return true;
    setClaimOptions(options);

    if (!options.hasClaimableAccess && !options.emailVerificationRequired) {
      // Claimed elsewhere (another tab): drop this tab's free-tier gates.
      await Promise.all([getSubscriptionStatus(), getUserInfo()]);
    }

    return Boolean(
      !options.hasClaimableAccess && options.emailVerificationRequired
    );
  };

  const dismissConfirmEmail = () => {
    if (currentUser?.id) {
      try {
        sessionStorage.setItem(confirmEmailDismissedKey(currentUser.id), '1');
      } catch {
        // Storage blocked: hidden until the next page load only.
      }
    }
    setIsConfirmEmailDismissed(true);
  };

  const dismissExpiredModal = () => {
    // Normalized store (authoritative, per-instance, cross-device).
    if (expiredMessageKey) {
      void recordTipDismissed(expiredMessageKey);
    }
    if (expiredDismissalKey) {
      localStorage.setItem(expiredDismissalKey, 'true');
      setDismissedExpiredKey(expiredDismissalKey);
    }
  };

  return (
    <>
      <InstitutionalAccessClaimModal
        open={showClaimModal}
        claimCopy={claimOptions?.claimCopy}
        schoolNames={schoolNames}
        accessEndDate={accessEndDate}
        claimError={claimError}
        isClaiming={isClaiming}
        onClaim={handleClaim}
        emailVerificationRequired={emailVerificationRequired}
        onRecheckEmail={recheckEmailVerification}
        onNotNowEmail={dismissConfirmEmail}
      />
      <InstitutionalAccessExpiredModal
        open={showExpiredModal}
        onClose={dismissExpiredModal}
        schoolName={expiredAccess?.schoolName ?? ''}
        endedDate={expiredEndedDate}
      />
    </>
  );
};

export default InstitutionalAccessModals;
